import { Box, Grid, TextField } from '@material-ui/core'
import InfoTooltip from 'components/InfoTooltip'
import PostCodeAddress from 'components/PostCodeAddress/PostCodeAddress'
import { ContactDataViewConfiguration, PostCodeDetail } from 'graphql/types'
import { useTranslation } from 'react-i18next'
import { Error, ViewType } from 'store/GeneralState/GeneralState.reducer'
import TestIDs from 'utils/TestIDs'
import Dropdown from '../Dropdown'

export interface ContactFieldsProps {
    errors?: Error[]

    testCategory: 'PERSONAL' | 'DELIVERY' | 'BILLING' | 'ACCOUNT_HOLDER'
    zip: string
    city: string
    district: string
    street: string
    houseNumber: string
    addition: string
    title: string
    countryAddr?: string
    company?: string
    companyLegalForm?: string
    companyRegisterEntry?: string
    companyLocation?: string
    companyId?: string
    lastName: string
    name: string
    salutation: string

    streetLabel?: string
    zipLabel?: string
    cityLabel?: string
    houseNumberLabel?: string
    additionalAddressInfoLabel?: string
    additionalAddressInfoText?: string

    zipLength?: number
    dhlEnabled?: boolean
    enableAddressAddition?: boolean
    enableAddressDistrict?: boolean
    showAutoComplete?: boolean
    showCountryAddress?: boolean
    showAdditionalAddressInfo?: boolean
    additionalInfoAddr?: string
    contactDataViewConfiguration?: ContactDataViewConfiguration
    salutationOptions: string[]
    isAddressDisabled: boolean
    isAddressRequired?: boolean

    setCity?: (value: string) => void
    setDistrict?: (value: string) => void
    setHouseNumber?: (value: string) => void
    setLastName: (value: string) => void
    setName: (value: string) => void
    setSalutation: (value: string) => void
    setStreet?: (value: string) => void
    setTitle: (value: string) => void
    setZip?: (value: string) => void
    setAddition?: (value: string) => void
    setAdditionalInfoAddr?: (value: string) => void
    setCountryAddr?: (value: string) => void
    setCompany?: (value: string) => void
    setCompanyLegalForm?: (value: string) => void
    setCompanyRegistrationEntry?: (value: string) => void
    setCompanyLocation?: (value: string) => void
    setCompanyId?: (value: string) => void
    handleAddressSelected?: (data: PostCodeDetail) => void
}

const ContactFields = (props: ContactFieldsProps): JSX.Element => {
    const { t } = useTranslation()
    const regexp = /^[0-9\b]+$/
    const {
        zip,
        city,
        district,
        street,
        houseNumber,
        addition,
        testCategory,
        title,
        salutation,
        name,
        lastName,
        company,
        companyLegalForm,
        companyRegisterEntry,
        companyLocation,
        companyId,
        salutationOptions,
        errors,
        countryAddr,
        streetLabel,
        zipLabel,
        zipLength,
        cityLabel,
        houseNumberLabel,
        additionalAddressInfoLabel,
        additionalAddressInfoText,
        dhlEnabled,
        enableAddressAddition,
        enableAddressDistrict,
        isAddressDisabled,
        isAddressRequired,
        showAutoComplete,
        showCountryAddress,
        showAdditionalAddressInfo,
        additionalInfoAddr,
        contactDataViewConfiguration,
        setZip,
        setCity,
        setDistrict,
        setCountryAddr,
        setStreet,
        setHouseNumber,
        setAddition,
        setAdditionalInfoAddr,
        setSalutation,
        setTitle,
        setLastName,
        setName,
        setCompany,
        setCompanyLegalForm,
        setCompanyRegistrationEntry,
        setCompanyLocation,
        setCompanyId,
        handleAddressSelected,
    } = props
    const setAddressSelected = (data: PostCodeDetail) => {
        if (handleAddressSelected) {
            handleAddressSelected(data)
        }
    }

    let enableLegalForm = false
    let enableLegalFormB2C = false
    let enableCompanyRegisterEntry = false
    let enableCompanyLocation = false
    let enableCompanyTaxID = false
    if (contactDataViewConfiguration) {
        enableLegalForm = contactDataViewConfiguration.enableAlternativeLegalForm
        enableCompanyRegisterEntry = contactDataViewConfiguration.enableCompanyRegisterEntry
        enableCompanyLocation = contactDataViewConfiguration.enableCompanyLocation
        enableCompanyTaxID = contactDataViewConfiguration.enableCompanyTaxID
        if (testCategory === 'PERSONAL') {
            enableLegalForm = contactDataViewConfiguration.enableLegalForm
            if (salutation === 'Firma') {
                enableLegalFormB2C = contactDataViewConfiguration.enableLegalFormB2C
            }
        }
    }

    const getZipErr = (): string => {
        if (errors) {
            switch (testCategory) {
                case 'ACCOUNT_HOLDER':
                    errors.find((err) => {
                        if (
                            err.category === ViewType.BANK_DETAILS &&
                            err.errors.find((error) => error === 'zipMustBe5Digits') !== undefined
                        ) {
                            return 'TextError'
                        }
                    })
                    return ''

                case 'BILLING':
                    errors.find((err) => {
                        if (
                            err.category === ViewType.CONTACT_DATA &&
                            err.errors.find((error) => error === 'billingZipMustBe5Digits') !== undefined
                        ) {
                            return 'TextError'
                        }
                    })
                    return ''

                case 'DELIVERY':
                    errors.find((err) => {
                        if (
                            err.category === ViewType.CONTACT_DATA &&
                            err.errors.find((error) => error === 'deliveryZipMustBe5Digits') !== undefined
                        ) {
                            return 'TextError'
                        }
                    })
                    return ''
                default:
                    return ''
            }
        } else {
            return ''
        }
    }

    return (
        <Grid container item spacing={1}>
            <Grid item xs={12} md={6}>
                <Dropdown
                    testIdentifier={{
                        input: 'SALUTATION',
                        viewType: ViewType.CONTACT_DATA,
                        category: testCategory,
                    }}
                    label={t('contactDataStrings.salutation')}
                    options={salutationOptions}
                    value={salutation}
                    onChange={(e): void => {
                        setSalutation(e.target.value as string)
                    }}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    {...TestIDs.GET_CONTACT_DATA('TITLE', testCategory)}
                    fullWidth={true}
                    label={t('contactDataStrings.title')}
                    variant={'outlined'}
                    value={title}
                    onChange={(e): void => setTitle(e.currentTarget.value)}
                    inputProps={{ maxLength: 30 }}
                />
            </Grid>
            {company !== undefined && setCompany && (
                <Grid item xs={12}>
                    <TextField
                        {...TestIDs.GET_CONTACT_DATA('COMPANY', testCategory)}
                        fullWidth={true}
                        label={t('contactDataStrings.companyName') + ' *'}
                        variant={'outlined'}
                        value={company}
                        onChange={(e): void => setCompany(e.currentTarget.value)}
                        inputProps={{ maxLength: 50 }}
                    />
                </Grid>
            )}
            {((enableLegalForm === true && company !== undefined && companyLegalForm !== undefined) ||
                enableLegalFormB2C) &&
                setCompanyLegalForm && (
                    <Grid item xs={12}>
                        <TextField
                            {...TestIDs.GET_CONTACT_DATA('COMPANY_LEGALFORM', testCategory)}
                            fullWidth={true}
                            label={t('contactDataStrings.companyLegalForm') + ' *'}
                            variant={'outlined'}
                            value={companyLegalForm}
                            onChange={(e): void => setCompanyLegalForm(e.currentTarget.value)}
                            inputProps={{ maxLength: 30 }}
                        />
                    </Grid>
                )}
            {enableCompanyRegisterEntry === true && companyRegisterEntry !== undefined && setCompanyRegistrationEntry && (
                <Grid item xs={12}>
                    <TextField
                        {...TestIDs.GET_CONTACT_DATA('COMPANY_REGISTRATION', testCategory)}
                        fullWidth={true}
                        label={t('contactDataStrings.companyRegisterEntry')}
                        variant={'outlined'}
                        value={companyRegisterEntry}
                        onChange={(e): void => setCompanyRegistrationEntry(e.currentTarget.value)}
                        inputProps={{ maxLength: 50 }}
                    />
                </Grid>
            )}

            {enableCompanyLocation === true && companyLocation !== undefined && setCompanyLocation && (
                <Grid item xs={12}>
                    <TextField
                        {...TestIDs.GET_CONTACT_DATA('COMPANY_LEGALFORM', testCategory)}
                        fullWidth={true}
                        label={t('contactDataStrings.companyLocation') + ' *'}
                        variant={'outlined'}
                        value={companyLocation}
                        onChange={(e): void => setCompanyLocation(e.currentTarget.value)}
                        inputProps={{ maxLength: 50 }}
                    />
                </Grid>
            )}
            {enableCompanyTaxID === true && companyId !== undefined && setCompanyId && (
                <Grid item xs={12}>
                    <TextField
                        {...TestIDs.GET_CONTACT_DATA('COMPANY_TAX_ID', testCategory)}
                        fullWidth={true}
                        label={t('contactDataStrings.companyTaxId') + ' *'}
                        variant={'outlined'}
                        value={companyId}
                        onChange={(e): void => setCompanyId(e.currentTarget.value)}
                        inputProps={{ maxLength: 50 }}
                    />
                </Grid>
            )}
            <Grid item xs={12}>
                <TextField
                    {...TestIDs.GET_CONTACT_DATA('NAME', testCategory)}
                    fullWidth={true}
                    label={t('contactDataStrings.name')}
                    variant={'outlined'}
                    value={name}
                    placeholder={'Max'}
                    onChange={(e): void => setName(e.currentTarget.value)}
                    inputProps={{ maxLength: 50 }}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    {...TestIDs.GET_CONTACT_DATA('LAST_NAME', testCategory)}
                    fullWidth={true}
                    label={t('contactDataStrings.lastName')}
                    variant={'outlined'}
                    value={lastName}
                    placeholder={'Mustermann'}
                    onChange={(e): void => setLastName(e.currentTarget.value)}
                    inputProps={{ maxLength: 50 }}
                />
            </Grid>
            {showAutoComplete ? (
                <Grid item xs>
                    <PostCodeAddress
                        data={{
                            district: district,
                            city: city,
                            houseNumber: houseNumber,
                            street: street,
                            zipcode: zip,
                            addition: addition,
                        }}
                        handleAddressSelect={setAddressSelected}
                        {...TestIDs.GET_CONTACT_DATA('AUTO_COMPLETE', testCategory)}
                        areRequiredFields={isAddressRequired}
                    />
                </Grid>
            ) : (
                <>
                    <Grid item xs={12} md={houseNumberLabel ? 8 : 10}>
                        <TextField
                            {...TestIDs.GET_CONTACT_DATA('STREET', testCategory)}
                            fullWidth={true}
                            label={
                                streetLabel
                                    ? isAddressDisabled
                                        ? streetLabel
                                        : streetLabel + ' *'
                                    : isAddressDisabled
                                    ? t('street')
                                    : t('street') + ' *'
                            }
                            variant={'outlined'}
                            value={dhlEnabled ? t('contactDataStrings.DHLpackstation') : street}
                            disabled={isAddressDisabled || dhlEnabled}
                            onChange={(e) => {
                                if (setStreet) {
                                    setStreet(e.currentTarget.value)
                                }
                            }}
                            inputProps={{ maxLength: 50 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={houseNumberLabel ? 4 : 2}>
                        <TextField
                            {...TestIDs.GET_CONTACT_DATA('HOUSE_NUMBER', testCategory)}
                            fullWidth={true}
                            label={
                                houseNumberLabel
                                    ? isAddressDisabled
                                        ? houseNumberLabel
                                        : houseNumberLabel + ' *'
                                    : isAddressDisabled
                                    ? t('number')
                                    : t('number') + ' *'
                            }
                            variant={'outlined'}
                            value={houseNumber}
                            disabled={isAddressDisabled}
                            onChange={(e) => {
                                if (setHouseNumber) {
                                    if (dhlEnabled) {
                                        setHouseNumber(e.currentTarget.value.replace(/[^0-9]/g, ''))
                                    } else {
                                        setHouseNumber(e.currentTarget.value)
                                    }
                                }
                            }}
                            inputProps={{ maxLength: houseNumberLabel ? (dhlEnabled ? 3 : 15) : 10 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            {...TestIDs.GET_CONTACT_DATA('ZIP', testCategory)}
                            fullWidth={true}
                            label={
                                zipLabel
                                    ? isAddressDisabled
                                        ? zipLabel
                                        : zipLabel + ' *'
                                    : isAddressDisabled
                                    ? t('ZIP')
                                    : t('ZIP') + ' *'
                            }
                            variant={'outlined'}
                            value={zip}
                            disabled={isAddressDisabled}
                            onChange={(e) => {
                                if (
                                    setZip &&
                                    ((e.currentTarget.value !== '' && regexp.test(e.currentTarget.value)) ||
                                        e.currentTarget.value === '')
                                ) {
                                    setZip(e.currentTarget.value)
                                }
                            }}
                            inputProps={{ maxLength: zipLength ? zipLength : 10, className: getZipErr() }}
                        />
                    </Grid>
                    <Grid item xs={12} md={9}>
                        <TextField
                            {...TestIDs.GET_CONTACT_DATA('CITY', testCategory)}
                            fullWidth={true}
                            label={
                                cityLabel
                                    ? isAddressDisabled
                                        ? cityLabel
                                        : cityLabel + ' *'
                                    : isAddressDisabled
                                    ? t('city')
                                    : t('city') + ' *'
                            }
                            variant={'outlined'}
                            value={city}
                            disabled={isAddressDisabled}
                            onChange={(e) => {
                                if (setCity) {
                                    setCity(e.currentTarget.value)
                                }
                            }}
                            inputProps={{ maxLength: dhlEnabled ? 30 : 50 }}
                        />
                    </Grid>
                    {enableAddressDistrict && (
                        <Grid item xs={12} md={12}>
                            <TextField
                                fullWidth={true}
                                label={t('district')}
                                variant={'outlined'}
                                value={district}
                                disabled={isAddressDisabled}
                                onChange={(e) => {
                                    if (setDistrict) {
                                        setDistrict(e.currentTarget.value)
                                    }
                                }}
                                inputProps={{ maxLength: 50 }}
                            />
                        </Grid>
                    )}
                    {enableAddressAddition && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth={true}
                                label={t('addition')}
                                variant={'outlined'}
                                disabled={isAddressDisabled}
                                value={addition ? addition : ''}
                                onChange={(e) => {
                                    if (setAddition) {
                                        setAddition(e.currentTarget.value)
                                    }
                                }}
                                inputProps={{ maxLength: 150 }}
                            />
                        </Grid>
                    )}
                    {showAdditionalAddressInfo && (
                        <Grid
                            item
                            xs={additionalAddressInfoText ? 10 : 12}
                            style={{ marginRight: additionalAddressInfoText ? 8 : 0 }}
                        >
                            <TextField
                                {...TestIDs.GET_CONTACT_DATA('ADDITIONAL_ADDR_INFO', testCategory)}
                                fullWidth={true}
                                label={
                                    additionalAddressInfoLabel ? additionalAddressInfoLabel : t('additionalInfoAddress')
                                }
                                variant={'outlined'}
                                value={additionalInfoAddr ? additionalInfoAddr : ''}
                                onChange={(e) => {
                                    if (setAdditionalInfoAddr) {
                                        if (dhlEnabled) {
                                            setAdditionalInfoAddr(e.currentTarget.value.replace(/[^0-9]/g, ''))
                                        } else {
                                            setAdditionalInfoAddr(e.currentTarget.value)
                                        }
                                    }
                                }}
                                inputProps={{ maxLength: dhlEnabled ? 10 : 150 }}
                            />
                        </Grid>
                    )}
                    {showAdditionalAddressInfo && additionalAddressInfoText && (
                        <Grid item>
                            <Box width={1} height={1} className={'InfoTooltipContainer'} display={'flex'}>
                                <InfoTooltip title={additionalAddressInfoText} />
                            </Box>
                        </Grid>
                    )}
                </>
            )}
            {showCountryAddress && (
                <Grid item xs={12}>
                    <TextField
                        {...TestIDs.GET_CONTACT_DATA('COUNTRY_ADDR_INFO', testCategory)}
                        fullWidth={true}
                        label={t('countryAddress')}
                        variant={'outlined'}
                        value={countryAddr ? countryAddr : ''}
                        onChange={(e) => {
                            if (setCountryAddr) {
                                setCountryAddr(e.currentTarget.value)
                            }
                        }}
                        inputProps={{ maxLength: 150 }}
                    />
                </Grid>
            )}
        </Grid>
    )
}

export default ContactFields
